import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, UserPlus } from "lucide-react";

import { transportService, type Allocation, type Route, type StudentOption } from "../../services/transportService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { SearchPick } from "../library/Pickers";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { CancelPanel, errorLine } from "./TransportParts";
import { PAYMENT_TYPES, allocationConfig, allocationStatus, canTakeRiders, clock, day, labelFrom, money, routeFee } from "./transportLogic";

const learnerLine = (learner: StudentOption) => [learner.class_name, learner.student_id].filter(Boolean).join(" · ");

function AllocateForm({ learners, routes, onClose, onDone }: { learners: StudentOption[]; routes: Route[]; onClose: () => void; onDone: (row: Allocation) => void }) {
  const open = routes.filter(canTakeRiders);
  const [learner, setLearner] = useState("");
  const [routeId, setRouteId] = useState("");
  const [payment, setPayment] = useState("term");
  const [amount, setAmount] = useState("");
  const route = open.find((row) => String(row.id) === routeId);
  const stops = [...(route?.stops ?? [])].filter((stop) => stop.is_active).sort((a, b) => a.sequence_order - b.sequence_order);
  const save = useMutation({ mutationFn: transportService.allocate, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const text = (name: string) => String(data.get(name) ?? "").trim() || null;
    save.mutate({
      student_id: Number(learner),
      route_id: Number(routeId),
      pickup_stop_id: Number(data.get("pickup")),
      dropoff_stop_id: Number(data.get("dropoff")),
      start_date: String(data.get("start")),
      end_date: text("end"),
      payment_type: payment,
      total_amount: amount,
      emergency_contact_name: text("contact_name"),
      emergency_contact_phone: text("contact_phone"),
      emergency_contact_relationship: text("contact_relationship"),
      special_requirements: text("special"),
      medical_conditions: text("medical"),
      notes: text("notes"),
    });
  };
  const pickRoute = (id: string) => {
    setRouteId(id);
    setAmount(routeFee(open.find((row) => String(row.id) === id), payment));
  };
  const pickPayment = (value: string) => {
    setPayment(value);
    setAmount(routeFee(route, value));
  };

  return (
    <FormModal
      id="allocate"
      title="Give a learner a place on a route"
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The place could not be given.") : undefined}
      saveLabel="Give the place"
      note="The place waits for approval before the learner rides."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The learner" divided={false}>
        <Field label="Learner" as="div" className="sm:col-span-2" hint="Learners who already have a place are left out.">
          <SearchPick
            items={learners}
            value={learner}
            onChange={setLearner}
            label={(row) => row.full_name}
            detail={learnerLine}
            text={(row) => `${row.full_name} ${row.student_id}`}
            placeholder="Search by name, class or learner number"
            empty="No learner matches"
            required
          />
        </Field>
      </FormSection>

      <FormSection title="The route">
        <Field label="Route" className="sm:col-span-2" hint={route ? `${route.available_spots} places free · leaves ${clock(route.departure_time)}` : "Full and closed routes are left out."}>
          <select required value={routeId} onChange={(event) => pickRoute(event.target.value)} className="input mt-1">
            <option value="">Choose a route</option>
            {open.map((row) => <option key={row.id} value={row.id}>{row.name} · {row.start_location} → {row.end_location}</option>)}
          </select>
        </Field>
        <Field label="Picked up at">
          <select required name="pickup" disabled={!route} className="input mt-1">
            <option value="">{route ? "Choose a stop" : "Choose the route first"}</option>
            {stops.filter((stop) => stop.is_pickup_point).map((stop) => <option key={stop.id} value={stop.id}>{stop.sequence_order}. {stop.name} · {clock(stop.arrival_time)}</option>)}
          </select>
        </Field>
        <Field label="Dropped off at">
          <select required name="dropoff" disabled={!route} className="input mt-1">
            <option value="">{route ? "Choose a stop" : "Choose the route first"}</option>
            {stops.filter((stop) => stop.is_dropoff_point).map((stop) => <option key={stop.id} value={stop.id}>{stop.sequence_order}. {stop.name}</option>)}
          </select>
        </Field>
        <Field label="Riding from">
          <input required type="date" name="start" defaultValue={todayIso()} className="input mt-1" />
        </Field>
        <Field label="Until" optional hint="Leave empty to keep riding.">
          <input type="date" name="end" className="input mt-1" />
        </Field>
        {route && !stops.length && <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900 sm:col-span-2">This route has no stops yet. Add them under Routes first.</p>}
      </FormSection>

      <FormSection title="Fee">
        <Field label="Billed">
          <select value={payment} onChange={(event) => pickPayment(event.target.value)} className="input mt-1">
            {PAYMENT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        <Field label="Amount (UGX)" hint="The route's fee for the period; change it for a discount or sibling rate.">
          <input required type="number" min={0} step="any" value={amount} onChange={(event) => setAmount(event.target.value)} className="input mt-1 tabular-nums" />
        </Field>
      </FormSection>

      <FormSection title="For the driver">
        <Field label="Who to call" optional>
          <input name="contact_name" maxLength={100} className="input mt-1" />
        </Field>
        <Field label="Phone" optional>
          <input type="tel" name="contact_phone" maxLength={20} className="input mt-1" placeholder="e.g. 0772 123456" />
        </Field>
        <Field label="Relationship" optional>
          <input name="contact_relationship" list="rider-relationships" maxLength={50} className="input mt-1" />
          <datalist id="rider-relationships">{["Mother", "Father", "Guardian", "Aunt", "Uncle", "Grandparent"].map((item) => <option key={item} value={item} />)}</datalist>
        </Field>
        <Field label="Health note" optional hint="e.g. asthma, inhaler in bag. Shown on the boarding sheet.">
          <input name="medical" className="input mt-1" />
        </Field>
        <Field label="Special needs" optional className="sm:col-span-2" hint="e.g. needs help with the steps; collected only by the mother.">
          <textarea name="special" rows={2} className="input mt-1" />
        </Field>
        <Field label="Notes" optional className="sm:col-span-2">
          <textarea name="notes" rows={2} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Who rides which route, from which stop, and whether their place is approved. */
export function RidersPanel({ allocations, routes, learners, isLoading, isError, onRetry, canManage, isFamily, onChanged , initialView}: {
  /** A register view to open on, e.g. "papers" from the dashboard. */
  initialView?: string;
  allocations?: Allocation[];
  routes: Route[];
  learners: StudentOption[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  isFamily: boolean;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const done = (text: string, close = false) => {
    setNotice({ tone: "success", text });
    if (close) setViewing(null);
    onChanged();
  };
  const approve = useMutation({ mutationFn: transportService.approveAllocation, onSuccess: (row) => done(`${row.student_name} can now ride ${row.route_name}.`) });
  const cancel = useMutation({ mutationFn: transportService.cancelAllocation, onSuccess: (row) => done(`${row.student_name}'s place on ${row.route_name} was cancelled; the seat is free again.`, true) });
  const classOf = new Map(learners.map((learner) => [learner.id, learner.class_name ?? ""]));
  const stops = new Map(routes.flatMap((route) => route.stops).map((stop) => [stop.id, stop]));
  const holding = new Set((allocations ?? []).filter((row) => ["pending", "active", "suspended"].includes(row.status)).map((row) => row.student));
  const free = learners
    .filter((learner) => !holding.has(learner.id))
    .sort((a, b) => (a.class_level_order ?? 999) - (b.class_level_order ?? 999) || a.full_name.localeCompare(b.full_name));
  const showsMoney = (allocations ?? []).some((row) => row.total_amount !== undefined);

  const columns: RegisterColumn<Allocation>[] = [
    {
      header: "Learner",
      cell: (row) => (
        <span>
          <span className="font-medium text-gray-900">{row.student_name}</span>
          <span className="block text-xs text-gray-500">{row.student_id}</span>
        </span>
      ),
      exportValue: (row) => row.student_name,
      className: "min-w-[10rem]",
    },
    { header: "Learner number", cell: (row) => row.student_id, exportValue: (row) => row.student_id, exportOnly: true },
    ...(learners.length ? [{ header: "Class", cell: (row: Allocation) => classOf.get(row.student) || "—", exportValue: (row: Allocation) => classOf.get(row.student) ?? "", className: "text-gray-700" }] : []),
    { header: "Route", cell: (row) => row.route_name, exportValue: (row) => row.route_name, className: "text-gray-700" },
    {
      header: "Picked up",
      cell: (row) => `${row.pickup_stop_name}${stops.get(row.pickup_stop) ? ` · ${clock(stops.get(row.pickup_stop)!.arrival_time)}` : ""}`,
      exportValue: (row) => row.pickup_stop_name,
      className: "text-gray-700",
    },
    { header: "Dropped off", cell: (row) => row.dropoff_stop_name, exportValue: (row) => row.dropoff_stop_name, className: "text-gray-700" },
    { header: "Contact", cell: (row) => row.emergency_contact_phone || "—", exportValue: (row) => [row.emergency_contact_name, row.emergency_contact_phone].filter(Boolean).join(" "), className: "text-gray-700" },
    ...(showsMoney
      ? [
          { header: "Fee", cell: (row: Allocation) => `${money(row.total_amount)} ${labelFrom(PAYMENT_TYPES, row.payment_type).toLowerCase()}`, exportValue: (row: Allocation) => Number(row.total_amount ?? 0), align: "right" as const },
          { header: "Not yet billed", cell: (row: Allocation) => money(row.unbilled_amount), exportValue: (row: Allocation) => Number(row.unbilled_amount ?? 0), align: "right" as const, exportOnly: true },
        ]
      : []),
    { header: "Status", cell: (row) => <StatusDot {...allocationStatus(row)} />, exportValue: (row) => allocationStatus(row).label },
  ];

  const row = viewing !== null ? allocations?.find((entry) => entry.id === viewing) : undefined;
  const pickup = row ? stops.get(row.pickup_stop) : undefined;
  const dropoff = row ? stops.get(row.dropoff_stop) : undefined;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title={isFamily ? "School bus places" : "Riders"}
        description={canManage ? "Open a place to approve it or cancel it." : isFamily ? "Which route your child rides, and the stops." : "Learners on your routes and who to call."}
        actions={canManage && (
          <button type="button" disabled={!routes.some(canTakeRiders)} onClick={() => setAdding(true)} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
            <UserPlus className="mr-1.5 h-3.5 w-3.5" /> Give a place
          </button>
        )}
        rows={allocations}
        config={allocationConfig}
        columns={columns}
        rowKey={(entry) => entry.id}
        onOpen={(entry) => {
          approve.reset();
          cancel.reset();
          setViewing(entry.id);
        }}
        openLabel={(entry) => (canManage && entry.status === "pending" ? "Approve" : "Open")}
        noun={["place", "places"]}
        exportName={{ file: "transport-riders", title: "Transport Riders" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "No learner has a place yet. Start with Give a place." : "No places on the school bus."}
        canExport={!isFamily}
        initialState={initialView ? { view: initialView } : (allocations ?? []).some((entry) => entry.status === "active") ? undefined : { view: "all" }}
        key={`${allocations ? "loaded" : "loading"}-${initialView ?? ""}`}
      />

      {row && (
        <DetailsModal
          title={`${row.student_name} · ${row.route_name}`}
          headline={{
            label: "Picked up",
            value: pickup ? `${clock(pickup.arrival_time)} at ${row.pickup_stop_name}` : row.pickup_stop_name,
            note: `Dropped off at ${row.dropoff_stop_name}${dropoff && dropoff.id !== pickup?.id ? ` (${clock(dropoff.arrival_time)})` : ""}`,
          }}
          status={allocationStatus(row)}
          facts={[
            ["Learner number", row.student_id],
            ["Class", classOf.get(row.student) || undefined],
            ["Riding from", day(row.start_date)],
            ["Until", day(row.end_date) || undefined],
            ["Who to call", [row.emergency_contact_name, row.emergency_contact_relationship && `(${row.emergency_contact_relationship})`].filter(Boolean).join(" ") || undefined],
            ["Phone", row.emergency_contact_phone ? <a key="phone" href={`tel:${row.emergency_contact_phone}`} className="text-primary-700 hover:underline">{row.emergency_contact_phone}</a> : undefined],
            ...(row.total_amount !== undefined
              ? ([
                  ["Fee", `${money(row.total_amount)} ${labelFrom(PAYMENT_TYPES, row.payment_type).toLowerCase()}`],
                  ["Paid", money(row.amount_paid)],
                  ["Not yet billed", money(row.unbilled_amount)],
                ] as [string, string][])
              : []),
            ["Given by", row.allocated_by_name],
            ["Approved by", row.approved_by_name],
          ]}
          sections={[
            { title: "Health note", body: row.medical_conditions, tone: "warning" },
            { title: "Special needs", body: row.special_requirements },
            { title: "Notes", body: row.notes },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && row.status === "pending" && (
                <button type="button" disabled={approve.isPending} onClick={() => approve.mutate(row.id)} className="btn btn-primary gap-1.5">
                  <CheckCircle2 className="h-4 w-4" /> Approve the place
                </button>
              )}
            </>
          }
        >
          {approve.isError && errorLine(approve.error, "The place could not be approved.")}
          {canManage && (row.status === "pending" || row.status === "active") && (
            <CancelPanel
              title="Cancel the place"
              description="The learner stops riding today and the seat is freed. Charges already raised stay with the bursar."
              placeholder="e.g. The family moved to Entebbe."
              context={{ key: "transport.note", params: { kind: "cancel", subject: "allocation", id: row.id } }}
              button="Cancel the place"
              busy={cancel.isPending}
              error={cancel.error}
              errorText="The place could not be cancelled."
              onCancel={(reason) => cancel.mutate({ id: row.id, reason })}
            />
          )}
        </DetailsModal>
      )}

      {adding && (
        <AllocateForm
          learners={free}
          routes={routes}
          onClose={() => setAdding(false)}
          onDone={(saved) => {
            setAdding(false);
            done(`${saved.student_name} has a place on ${saved.route_name}, waiting for approval.`);
            setViewing(saved.id);
          }}
        />
      )}
    </div>
  );
}
